import React, { useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate, useParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import moment from 'moment';
import { Layout } from './components/Layout/Layout';
import { MalattiaFormStyles, MalattiaQuestionsStyles, QuestionLabel } from '../MalattiaProduct/styles/MalattiaFormStyles.styles';
import { FormInputRadio } from '../../common/formInputsV2/FormInputRadio';
import { flagResponse, genders } from '../../common/constants';
import { FormInputText } from '../../common/formInputsV2/FormInputText';
import { FormInputDate } from '../../common/formInputsV2/FormInputDate';
import FormReactSelectInputBirthPlace from '../../common/formInputsV2/FormReactSelectInputBirthPlace';
import { FormInputAddressCompleteManual } from '../../common/formInputsV2/FormInputAddressCompleteManual';
import { FormInputAddressComplete } from '../../common/formInputsV2/FormInputAddressComplete';
import { greeniaPrivacyPolicyBackOffice } from '../../services/axios-client/axeCommonsBackOffice';
import InputPrivacy from '../../common/componentsV2/inputs/InputPrivacy';
import PageLoading from '../../common/componentsV2/elements/PageLoading';
import { answersMalattiaFieldsBySteps, malattiaSchema } from './BackofficeMalattia.validations';
import { malattiaGetInitialData, malattiaSubmitAnswers, resetMalattiaForm } from '../../features/formsBackoffice/formsBackofficeActions';
import generateFiscalCode from '../../common/helpers/generateFiscalCode';
import { generalAlertError } from '../../common/utils/alerts';
import { modifyRequestDataBackoffice } from '../../services/axios-client/axeCommons';
import getMinAllowedBirthday from '../../common/helpers/getMinAllowedBirthday';
import { getBlockingFields, buildMissingFieldsMessage } from '../MalattiaProduct/MalattiaFormFlow.stepErrors';
import { V2FontImport } from '../../common/formInputsV2/V2Fonts';
import { FormLayout } from '../../common/formInputsV2/FormLayout.styles';
import { SurfaceCardHeading } from '../../common/formInputsV2/Card.styles';
import { FooterNav } from '../../common/formInputsV2/FooterNav.styles';
import { PrimaryButton, SecondaryButton } from '../../common/formInputsV2/Button.styles';
import ProgressBar from '../../common/componentsV2/ProgressBar';
import { sonnerErrorAlert } from '../../common/utils/sonnerAlerts';

const answersToCheck = [
  'invalidita_permanente_inabilita_temporanea',
  'infortuni_subiti',
  'rifiuto_o_condizioni_speciali_polizza',
  'polizza_malattia_annullata',
  'familiare_deceduto_prima_55_malattia',
  'familiari_primo_grado_casi_malattie_ereditarie',
  'imperfezioni_fisiche_o_disturbi_funizonali',
  'uso_sostanze_stupefacenti_o_abuso_alcolici',
  'fuma_piu_20_sigarette_al_giorno',
  'malattie_ricovero_ospedaliero',
  'disturbi_ricorrenti_malattia',
  'infortuni_ricovero_postumi_invalidanti',
  'farmaci_terapeutici_continuativi',
  'terapie_emotrasfusioni_chemioterapia_radioterapia',
  'malattie_infezioni_controlli_periodici',
  'pressione_arteriosa_inferiore_140_90',
  'colesterolo_totale',
  'diabete_mellito',
  'altre_assicurazioni_stessi_rischi',
];

function BackofficeMalattia() {
  const navigate = useNavigate();
  const location = useLocation();
  const { requestToken } = useParams();

  const skipCheckGenderRef = useRef(null);
  const state = useSelector((store) => store.formsBackoffice.malattia);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [showManualAdress, setShowManualAdress] = useState(false);
  const {
    loading,
    states,
    municipalities,
    dataPrivacy,
  } = state;
  const {
    handleSubmit,
    register,
    formState: { errors, touchedFields },
    setValue,
    trigger,
    getValues,
    watch,
  } = useForm({
    mode: 'onTouched',
    resolver: yupResolver(malattiaSchema),
    shouldFocusError: false,
    defaultValues: {
      product: 'malattia',
      source: 'greenia.it',
      policy_effective_date: '',
      privacies: [],
      name: '',
      surname: '',
      email: '',
      fiscal_code: '',
      address: '',
      house_number: '',
      postal_code: '',
      phone: '',
      gender: '',
      date_of_birth: '',
      country_of_birth_code: 'Z000',
      province_of_birth_code: null,
      commune_of_birth_code: null,
      born_abroad: false,
      residence_commune_code: '',
      residence_province_code: '',
      active_customer_id: null,
      existing_customer: false,
      invalidita_permanente_inabilita_temporanea: '',
      infortuni_subiti: '',
      rifiuto_o_condizioni_speciali_polizza: '',
      polizza_malattia_annullata: '',
      familiare_deceduto_prima_55_malattia: '',
      familiari_primo_grado_casi_malattie_ereditarie: '',
      imperfezioni_fisiche_o_disturbi_funizonali: '',
      uso_sostanze_stupefacenti_o_abuso_alcolici: '',
      fuma_piu_20_sigarette_al_giorno: '',
      malattie_ricovero_ospedaliero: '',
      disturbi_ricorrenti_malattia: '',
      infortuni_ricovero_postumi_invalidanti: '',
      farmaci_terapeutici_continuativi: '',
      terapie_emotrasfusioni_chemioterapia_radioterapia: '',
      malattie_infezioni_controlli_periodici: '',
      pressione_arteriosa_inferiore_140_90: '',
      colesterolo_totale: '',
      diabete_mellito: '',
      altre_assicurazioni_stessi_rischi: '',

    },
  });

  const formDataFlow = watch();

  const updateFormData = (data) => {
    const keys = Object.keys(data);
    keys.forEach((key) => {
      setValue(key, data[key], {
        shouldValidate: true,
        shouldDirty: true,
        shouldTouch: true,
      });
    });
    trigger(keys);
  };

  const isValid = (name) => {
    if (errors[name]?.message !== undefined) {
      return false;
    }

    if (touchedFields[name] === undefined) {
      return false;
    }

    return true;
  };

  const scrollToFirstError = () => {
    setTimeout(() => {
      const el = document.querySelector('.is-invalid, [aria-invalid="true"], .general-input-error, .invalid-feedback.d-block');
      if (el && typeof el.scrollIntoView === 'function') {
        el.scrollIntoView({ behavior: 'smooth', block: 'center' });
      }
    }, 300);
  };

  const privacyItemChangeHandler = (value, item) => {
    if (!value.target.checked) {
      const newArray = [...formDataFlow.privacies];
      const index = newArray.indexOf(item.id);
      newArray.splice(index, 1);
      updateFormData({
        privacies: newArray,
      });
      return;
    }
    const answersNewArray = [...formDataFlow.privacies];
    answersNewArray.push(item.id);
    updateFormData({
      privacies: answersNewArray,
    });
  };

  useEffect(() => {
    malattiaGetInitialData();

    return () => {
      resetMalattiaForm();
    };
  }, []);

  const getFiscalCode = (birthDate) => {
    const d = birthDate.split('-');
    const fData = {
      name: formDataFlow.name,
      surname: formDataFlow.surname,
      gender: formDataFlow.gender,
      day: d[2],
      month: d[1],
      year: d[0],
    };
    if (formDataFlow.born_abroad) {
      fData.birthplace = states.filter(
        (s) => s.state_code === formDataFlow.country_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = 'EE';
    } else {
      fData.birthplace = municipalities.filter(
        (municipality) => municipality.cadastral_code === formDataFlow.commune_of_birth_code,
      )[0].name;
      fData.birthplaceProvincia = formDataFlow.province_of_birth_code;
    }
    return generateFiscalCode(fData);
  };

  const [showPrivacyErrors, setShowPrivacyErrors] = useState(false);

  const validateDataPrivacy = () => {
    let { error, message } = '';

    dataPrivacy.forEach((privacy) => {
      if (privacy.required === 1) {
        const userDataArray = { ...formDataFlow }; // make a separate copy of the object
        const index = userDataArray.privacies.indexOf(privacy.id);

        if (index < 0) {
          error = true;
          message = 'Tutti i campi sono obbligatori! Tutta la privacy con la stella deve essere controllata';
        }
      }
    });
    return { error, message };
  };

  const submitForm = async (formDataFlow) => {
    // Validate first — getFiscalCode() below throws on an empty
    // date_of_birth/commune_of_birth_code (looks up a municipality that
    // doesn't exist), which would otherwise blow up silently as an unhandled
    // rejection before the broker ever sees a validation error.
    const stepFields = answersMalattiaFieldsBySteps[currentIndex - 1];
    const validateQuestions = await trigger(stepFields);
    if (validateQuestions !== true) {
      const blocking = getBlockingFields(errors, stepFields);
      sonnerErrorAlert(buildMissingFieldsMessage(blocking));
      scrollToFirstError();
      return;
    }

    const _userData = { ...formDataFlow };

    if (_userData.born_abroad && _userData.country_of_birth_code === 'Z000') {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
      return;
    }

    if (
      _userData.born_abroad
      && (_userData.country_of_birth_code === null
        || _userData.country_of_birth_code === ''
        || _userData.country_of_birth_code == undefined)
    ) {
      window.scrollTo(0, 0);
      sonnerErrorAlert('Devi selezionare uno stato estero!');
      return;
    }

    const validation = validateDataPrivacy();
    if (validation.error) {
      setShowPrivacyErrors(true);
      sonnerErrorAlert(validation.message);
      scrollToFirstError();
      return;
    }

    if (
      formDataFlow.date_of_birth !== ''
      && formDataFlow.date_of_birth !== null
    ) {
      _userData.date_of_birth = moment(formDataFlow.date_of_birth).format(
        'YYYY-MM-DD',
      );
    } else {
      _userData.date_of_birth = null;
    }
    _userData.policy_effective_date = moment(
      formDataFlow.policy_effective_date,
    ).format('YYYY-MM-DD');
    _userData.fiscal_code = formDataFlow.gender !== 'G'
      ? getFiscalCode(formDataFlow.date_of_birth)
      : null;
    delete _userData.existing_customer;

    const hasInvalidAnswer = answersToCheck.some(
      (field) => formDataFlow[field] == 1,
    );

    if (hasInvalidAnswer) {
      generalAlertError('Il prodotto non è adeguato alle esigenze del cliente');
      return;
    }
    malattiaSubmitAnswers(_userData, navigate, requestToken);
  };

  const nextButton = async () => {
    const stepFields = answersMalattiaFieldsBySteps[currentIndex - 1];
    const res = await trigger(stepFields);

    if (res !== true) {
      const blocking = getBlockingFields(errors, stepFields);
      sonnerErrorAlert(buildMissingFieldsMessage(blocking));
      scrollToFirstError();
      return;
    }

    if (currentIndex === 1) {
      if (formDataFlow.born_abroad && formDataFlow.country_of_birth_code === 'Z000') {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Non puoi selezionare Italia come stato estero!');
        return;
      }

      if (
        formDataFlow.born_abroad
        && (formDataFlow.country_of_birth_code === null
          || formDataFlow.country_of_birth_code === ''
          || formDataFlow.country_of_birth_code == undefined)
      ) {
        window.scrollTo(0, 0);
        sonnerErrorAlert('Devi selezionare uno stato estero!');
        return;
      }

      const validation = validateDataPrivacy();
      if (validation.error) {
        setShowPrivacyErrors(true);
        sonnerErrorAlert(validation.message);
        scrollToFirstError();
        return;
      }
    }

    if (currentIndex === 2) {
      submitForm();
      return;
    }

    setCurrentIndex(currentIndex + 1);
    setTimeout(() => window.scrollTo(0, 0), 500);
  };

  useEffect(() => {
    if (skipCheckGenderRef.current === true) {
      skipCheckGenderRef.current = false;
      return;
    }

    const triggerValidation = {
      shouldTouch: true,
      shouldDirty: true,
      shouldValidate: true,
    };

    if (
      ['M', 'F'].includes(formDataFlow.gender)
      && formDataFlow.date_of_birth === null
    ) {
      setValue('date_of_birth', '', triggerValidation);
      setValue('country_of_birth_code', 'Z000', triggerValidation);
      setValue('province_of_birth_code', null, triggerValidation);
      setValue('commune_of_birth_code', null, triggerValidation);
      setValue('born_abroad', false, triggerValidation);
      setValue('name', '', triggerValidation);
      setValue('surname', '', triggerValidation);
    }
  }, [formDataFlow.gender, formDataFlow.date_of_birth]);

  // On entering a step, mark that step's prefilled fields touched and validate
  // only them — so defaults show green borders without firing errors for
  // fields the user has not reached yet.
  useEffect(() => {
    const values = getValues();
    if (values === undefined) {
      return;
    }

    const stepFields = answersMalattiaFieldsBySteps[currentIndex - 1] || [];
    const filledKeys = stepFields.filter(
      (key) => values[key] !== '' && values[key] !== null && values[key] !== undefined,
    );
    if (filledKeys.length === 0) {
      return;
    }
    filledKeys.forEach((key) => {
      setValue(key, values[key], { shouldTouch: true, shouldDirty: true });
    });
    trigger(filledKeys);
  }, [currentIndex]);

  useEffect(() => {
    const callAsync = async () => {
      const {
        data: {
          data: { userData },
        },
      } = await modifyRequestDataBackoffice(requestToken);
      updateFormData(userData);
    };

    callAsync();
  }, [location]);

  if (loading) {
    return <PageLoading />;
  }
  const stepsQuote = [
    'Dati Personali',
    'Questionario Anamnestico',
  ];

  return (
    <Layout>
      <V2FontImport />
      <div className="d-flex align-content-center justify-content-center">
        <ProgressBar activeStep={currentIndex} steps={stepsQuote} />
      </div>

      {currentIndex === 1 && (
        <MalattiaFormStyles
          onSubmit={handleSubmit(submitForm)}
        >
          <div className="salute-tutorial">

            <FormLayout className="form-container">
              <FormInputRadio
                options={genders.filter((g) => g.value !== 'G')}
                label="Il contraente è"
                name="gender"
                registration={register('gender')}
                error={errors.gender?.message}
              />

              <div>
                <div>
                  <FormInputText
                    placeholder="Nome"
                    label="Nome"
                    registration={register('name')}
                    error={errors.name?.message}
                    valid={isValid('name')}
                  />
                  <FormInputText
                    placeholder="Cognome"
                    label="Cognome"
                    registration={register('surname')}
                    error={errors.surname?.message}
                    valid={isValid('surname')}
                  />
                  <FormInputDate
                    label="Data di nascita"
                    registration={register('date_of_birth')}
                    minDate={getMinAllowedBirthday()}
                    maxDate="2021-01-01"
                    error={errors.date_of_birth?.message}
                    valid={isValid('date_of_birth')}
                  />
                  <FormReactSelectInputBirthPlace
                    states={states}
                    options={municipalities}
                    label="Luogo di nascita"
                    name="commune_of_birth_code"
                    values={{
                      commune_of_birth_code: formDataFlow.commune_of_birth_code,
                      province_of_birth_code:
                        formDataFlow.province_of_birth_code,
                      born_abroad: formDataFlow.born_abroad,
                      country_of_birth_code: formDataFlow.country_of_birth_code,
                    }}
                    onChange={(value) => updateFormData({
                      commune_of_birth_code: value.commune_of_birth_code,
                      province_of_birth_code: value.province_of_birth_code,
                      born_abroad: value.born_abroad,
                      country_of_birth_code: value.country_of_birth_code,
                    })}
                    valid={isValid('commune_of_birth_code')}
                    error={errors.commune_of_birth_code?.message}
                    valid_country_of_birth_code={isValid(
                      'country_of_birth_code',
                    )}
                    error_country_of_birth_code={
                      errors.country_of_birth_code?.message
                    }
                  />
                </div>
              </div>
              <FormInputText
                registration={register('email')}
                label="E-mail"
                placeholder="nome@esempio.it"
                error={errors.email?.message}
                valid={isValid('email')}
              />
              <FormInputText
                registration={register('phone')}
                label="Phone"
                placeholder="Es. 3401234567"
                paragraph="Numeri di rete fissa non accettati dalle aziende"
                error={errors.phone?.message}
                valid={isValid('phone')}
              />

              {showManualAdress ? (
                <FormInputAddressCompleteManual
                  municipalities={municipalities}
                  formData={formDataFlow}
                  isValid={isValid}
                  errors={errors}
                  register={register}
                  updateFormData={updateFormData}
                />
              ) : (
                <FormInputAddressComplete
                  municipalities={municipalities}
                  formData={formDataFlow}
                  isValid={isValid}
                  errors={errors}
                  register={register}
                  updateFormData={updateFormData}
                  addressId="autocomplete-address-contractor"
                  setShow={setShowManualAdress}
                />
              )}

              <FormInputDate
                label="Data Decorrenza"
                registration={register('policy_effective_date')}
                minDate={moment().format('YYYY-MM-DD')}
                error={errors.policy_effective_date?.message}
                valid={isValid('policy_effective_date')}
              />
            </FormLayout>
          </div>
          <FormLayout>
            <div className="terms">
              <SurfaceCardHeading>Informativa Privacy e IVASS</SurfaceCardHeading>

              <div className="text-decoration-underline mt-1 mb-2">
                <a
                  onClick={() => greeniaPrivacyPolicyBackOffice()}
                  style={{ cursor: 'pointer' }}
                >
                  <small>Greenia Privacy Policy</small>
                </a>
              </div>
              {dataPrivacy.map((privacy, i) => (
                <InputPrivacy
                  required={privacy.required}
                  label={privacy.content}
                  name={privacy.type}
                  id={privacy.type}
                  key={i}
                  checked={formDataFlow?.privacies?.includes(privacy.id)}
                  hasError={showPrivacyErrors && privacy.required === 1 && !formDataFlow?.privacies?.includes(privacy.id)}
                  onChange={(value) => privacyItemChangeHandler(value, privacy)}
                />
              ))}
            </div>
          </FormLayout>

        </MalattiaFormStyles>
      )}

      {currentIndex === 2 && (
        <MalattiaQuestionsStyles
          className=" mx-auto"
        >

          <FormLayout>
            <div>
              <QuestionLabel>
                Le è mai stata riconosciuta da società di assicurazione una invalidità permanente o una inabilità
                temporanea, fruisce di pensione di invalidità o ha presentato domanda per ottenerla, gode della
                qualifica di invalido civile?
              </QuestionLabel>
              <FormInputRadio
                name="invalidita_permanente_inabilita_temporanea"
                registration={register('invalidita_permanente_inabilita_temporanea')}
                error={errors.invalidita_permanente_inabilita_temporanea?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha mai subito infortuni?
              </QuestionLabel>
              <FormInputRadio
                name="infortuni_subiti"
                registration={register('infortuni_subiti')}
                error={errors.infortuni_subiti?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                E' mai stata rifiutata od accettata a condizioni speciali da parte di un'altra società di assicurazione una
                sua richiesta di polizza malattia, infortunio o vita?
              </QuestionLabel>
              <FormInputRadio
                name="rifiuto_o_condizioni_speciali_polizza"
                registration={register('rifiuto_o_condizioni_speciali_polizza')}
                error={errors.rifiuto_o_condizioni_speciali_polizza?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Le è mai stata annullata per sinistro una polizza malattia?
              </QuestionLabel>
              <FormInputRadio
                name="polizza_malattia_annullata"
                registration={register('polizza_malattia_annullata')}
                error={errors.polizza_malattia_annullata?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha mai avuto un genitore, fratello o sorella deceduto prima dei 55 anni a causa di malattia?
              </QuestionLabel>
              <FormInputRadio
                name="familiare_deceduto_prima_55_malattia"
                registration={register('familiare_deceduto_prima_55_malattia')}
                error={errors.familiare_deceduto_prima_55_malattia?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Tra i suoi familiari di i° grado (genitori, fratelli) si sono mai verificati casi di infarto miocardico, ictus
                cerebrale, tumori maligni, diabete mellito o altre malattie ereditarie?
              </QuestionLabel>
              <FormInputRadio
                name="familiari_primo_grado_casi_malattie_ereditarie"
                registration={register('familiari_primo_grado_casi_malattie_ereditarie')}
                error={errors.familiari_primo_grado_casi_malattie_ereditarie?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha imperfezioni fisiche o disturbi funzionali ( come deviazione del setto nasale, strabismo, scoliosi,
                sordità)?
              </QuestionLabel>
              <FormInputRadio
                name="imperfezioni_fisiche_o_disturbi_funizonali"
                registration={register('imperfezioni_fisiche_o_disturbi_funizonali')}
                error={errors.imperfezioni_fisiche_o_disturbi_funizonali?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha mai fatto o fa uso di sostanze stupefacenti, abuso di alcolici?
              </QuestionLabel>
              <FormInputRadio
                name="uso_sostanze_stupefacenti_o_abuso_alcolici"
                registration={register('uso_sostanze_stupefacenti_o_abuso_alcolici')}
                error={errors.uso_sostanze_stupefacenti_o_abuso_alcolici?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Fuma più di 20 sigarette al giorno?
              </QuestionLabel>
              <FormInputRadio
                name="fuma_piu_20_sigarette_al_giorno"
                registration={register('fuma_piu_20_sigarette_al_giorno')}
                error={errors.fuma_piu_20_sigarette_al_giorno?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Soffre o ha sofferto in passato di malattie che abbiano comportato un ricovero ospedaliero (con o
                senza intervento chirurgico) per cause diverse da appendicectomia, tonsillectomia, adenoidectomia o
                parto?
              </QuestionLabel>
              <FormInputRadio
                name="malattie_ricovero_ospedaliero"
                registration={register('malattie_ricovero_ospedaliero')}
                error={errors.malattie_ricovero_ospedaliero?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Soffre attualmente di disturbi ricorrenti o malattia?
              </QuestionLabel>
              <FormInputRadio
                name="disturbi_ricorrenti_malattia"
                registration={register('disturbi_ricorrenti_malattia')}
                error={errors.disturbi_ricorrenti_malattia?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha subito in passato infortuni che abbiano causato un ricovero ospedaliero (con o senza intervento
                chirurgico) dai quali siano derivati postumi invalidanti?
              </QuestionLabel>
              <FormInputRadio
                name="infortuni_ricovero_postumi_invalidanti"
                registration={register('infortuni_ricovero_postumi_invalidanti')}
                error={errors.infortuni_ricovero_postumi_invalidanti?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Assume farmaci a scopo terapeutico in modo continuativo?
              </QuestionLabel>
              <FormInputRadio
                name="farmaci_terapeutici_continuativi"
                registration={register('farmaci_terapeutici_continuativi')}
                error={errors.farmaci_terapeutici_continuativi?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                E' stato mai sottoposto a emotrasfusioni e/o terapie a base di emoderivati; a chemioterapia o a
                radioterapia?
              </QuestionLabel>
              <FormInputRadio
                name="terapie_emotrasfusioni_chemioterapia_radioterapia"
                registration={register('terapie_emotrasfusioni_chemioterapia_radioterapia')}
                error={errors.terapie_emotrasfusioni_chemioterapia_radioterapia?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                E' a conoscenza di essere affetto/a da qualche malattia o infezione ( ad esempio virus dell'epatite, hiv,
                ecc) che, pur non avendo determinato ricovero, richiede specifiche terapie o esami e accertamenti
                periodici?
              </QuestionLabel>
              <FormInputRadio
                name="malattie_infezioni_controlli_periodici"
                registration={register('malattie_infezioni_controlli_periodici')}
                error={errors.malattie_infezioni_controlli_periodici?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                I suoi valori di pressione arteriosa sono abitualmente inferiori a 140/90?
              </QuestionLabel>
              <FormInputRadio
                name="pressione_arteriosa_inferiore_140_90"
                registration={register('pressione_arteriosa_inferiore_140_90')}
                error={errors.pressione_arteriosa_inferiore_140_90?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                I suoi valori di colesterolemia totale sono abitualmente inferiori a 250 mg/dl?
              </QuestionLabel>
              <FormInputRadio
                name="colesterolo_totale"
                registration={register('colesterolo_totale')}
                error={errors.colesterolo_totale?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Le risulta essere affetto da diabete mellito?
              </QuestionLabel>
              <FormInputRadio
                name="diabete_mellito"
                registration={register('diabete_mellito')}
                error={errors.diabete_mellito?.message}
                options={flagResponse}
              />
            </div>
            <div>
              <QuestionLabel>
                Ha in corso altre assicurazioni per gli stessi rischi?
              </QuestionLabel>
              <FormInputRadio
                name="altre_assicurazioni_stessi_rischi"
                registration={register('altre_assicurazioni_stessi_rischi')}
                error={errors.altre_assicurazioni_stessi_rischi?.message}
                options={flagResponse}
              />
            </div>
          </FormLayout>

        </MalattiaQuestionsStyles>
      )}
      <FooterNav className="footer-buttons button-travel-step">
        <div className="d-flex justify-content-between">
          {currentIndex !== 1 && (
            <SecondaryButton
              type="button"
              className="btn btn-questionnaire back backtoStepOne backtoStepTwo backtoStepThree"
              onClick={() => setCurrentIndex(1)}
            >
              Indietro
            </SecondaryButton>
          )}
          {currentIndex === 1 ? (
            <PrimaryButton
              type="button"
              onClick={() => nextButton()}
              className="btn btn-questionnaire"
            >
              Continua
            </PrimaryButton>
          ) : (
            <PrimaryButton type="button" onClick={() => submitForm(formDataFlow)} className="btn btn-questionnaire">
              Vai a preventivo
            </PrimaryButton>
          )}
        </div>
      </FooterNav>

    </Layout>
  );
}

export default BackofficeMalattia;
